import { useMemo } from 'react';

const H_GAP = 140;  // horizontal gap between siblings
const V_GAP = 160;  // vertical gap between generations

/**
 * Computes (x, y) positions for each node using a generation-based layout.
 * Cousin nodes within the same generation are shifted slightly to the right
 * of non-cousin members to visually separate lateral branches.
 */
export function useFamilyTreeLayout(nodes = [], svgWidth = 900) {
  return useMemo(() => {
    if (!nodes.length) return {};

    const COUSIN_RELATIONS = ['Cousin', 'Cousin (once removed)', 'Cousin (twice removed)'];

    // Group by generation, separating cousins from direct family within each row
    const byGen = {};
    nodes.forEach(n => {
      const g = n.generation ?? 0;
      if (!byGen[g]) byGen[g] = { direct: [], cousins: [] };
      if (COUSIN_RELATIONS.includes(n.relation)) {
        byGen[g].cousins.push(n);
      } else {
        byGen[g].direct.push(n);
      }
    });

    const generations = Object.keys(byGen).map(Number).sort((a, b) => a - b);
    const minGen = generations[0];
    const positions = {};

    generations.forEach(gen => {
      const { direct, cousins } = byGen[gen];
      const allInRow = [...direct, ...cousins];
      const y = (gen - minGen) * V_GAP + 80;

      if (cousins.length === 0) {
        // Standard centred layout — no cousins
        const totalW = (allInRow.length - 1) * H_GAP;
        const startX = svgWidth / 2 - totalW / 2;
        allInRow.forEach((node, i) => {
          positions[node.id] = { x: startX + i * H_GAP, y };
        });
      } else {
        // Direct family centred; cousins offset to the right with a gap
        const COUSIN_GAP = H_GAP * 0.6; // extra gap between direct & cousin columns

        const directW  = Math.max(0, (direct.length - 1)) * H_GAP;
        const cousinW  = Math.max(0, (cousins.length - 1)) * H_GAP;
        const totalW   = directW + (direct.length > 0 && cousins.length > 0 ? COUSIN_GAP : 0) + cousinW;
        const startX   = svgWidth / 2 - totalW / 2;

        direct.forEach((node, i) => {
          positions[node.id] = { x: startX + i * H_GAP, y };
        });

        const cousinStartX = startX + directW + (direct.length > 0 ? COUSIN_GAP : 0);
        cousins.forEach((node, i) => {
          positions[node.id] = { x: cousinStartX + i * H_GAP, y };
        });
      }
    });

    return positions;
  }, [nodes, svgWidth]);
}